import { Upload } from 'lucide-react';
import {
  useCallback,
  useId,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface FileDropzoneProps {
  /** Comma-separated MIME list (e.g. "image/jpeg,image/png").
   *  Validated client-side before `onFilesSelected` fires. */
  accept: string;
  /** Per-file cap (bytes). Files over this size are rejected via
   *  `onError` before reaching `onFilesSelected`. */
  maxSizeBytes: number;
  /** Maximum number of files in a single drop / pick. Default: 1. */
  maxFiles?: number;
  /** Called with the validated File list (length ≤ maxFiles). */
  onFilesSelected: (files: File[]) => void;
  /** Called with a friendly i18n key + params for any rejection
   *  reason (too many, too large, wrong type). The host renders
   *  the error inline (Alert / Toast). */
  onError?: (err: { key: string; params?: Record<string, unknown> }) => void;
  /** Disables drop + click + file picker entirely. */
  disabled?: boolean;
  /** Optional label rendered above the icon. Defaults to a generic
   *  "drag here" copy via i18n. */
  label?: ReactNode;
  /** Extra class on the outer container. */
  className?: string;
}

/**
 * FileDropzone — generic drag-and-drop / click-to-pick input.
 *
 * Used wherever a feature needs the SPA to surface a file picker
 * with consistent UX:
 *   - vessel photo + documents (L1.3)
 *   - part documents (L2)
 *   - WO attachments (later)
 *
 * Validation runs in this order, short-circuiting on the first
 * failure: too-many → wrong-type → too-large. The host gets a
 * structured error key (e.g. `fileDropzone.tooLarge`) so it can
 * translate inline.
 *
 * Click-to-pick uses a hidden `<input type="file">`; the dropzone
 * label triggers it via a ref. No native styling escapes.
 */
export function FileDropzone({
  accept,
  maxSizeBytes,
  maxFiles = 1,
  onFilesSelected,
  onError,
  disabled,
  label,
  className,
}: FileDropzoneProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const acceptList = accept
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const validateAndEmit = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      if (files.length > maxFiles) {
        onError?.({
          key: 'fileDropzone.tooMany',
          params: { max: maxFiles, got: files.length },
        });
        return;
      }
      const wrongType = files.find((f) => !acceptList.includes(f.type));
      if (wrongType) {
        onError?.({
          key: 'fileDropzone.wrongType',
          params: { type: wrongType.type, allowed: acceptList.join(', ') },
        });
        return;
      }
      const tooBig = files.find((f) => f.size > maxSizeBytes);
      if (tooBig) {
        onError?.({
          key: 'fileDropzone.tooLarge',
          params: { name: tooBig.name, max: maxSizeBytes, got: tooBig.size },
        });
        return;
      }
      onFilesSelected(files);
    },
    [acceptList, maxFiles, maxSizeBytes, onError, onFilesSelected],
  );

  const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    validateAndEmit(files);
  };

  const handleDragOver = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  return (
    <label
      htmlFor={inputId}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed p-6 text-center text-sm transition-colors',
        isDragOver
          ? 'border-primary bg-primary/5 text-primary'
          : 'border-muted-foreground/30 text-muted-foreground hover:border-muted-foreground/60',
        disabled && 'cursor-not-allowed opacity-50 hover:border-muted-foreground/30',
        className,
      )}
    >
      <Upload aria-hidden className="h-6 w-6" />
      <span className="font-medium">
        {label ?? t('fileDropzone.dragHere')}
      </span>
      <span className="text-xs">{t('fileDropzone.orClick')}</span>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        multiple={maxFiles > 1}
        accept={accept}
        disabled={disabled}
        className="sr-only"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          validateAndEmit(files);
          // reset so picking the same file twice still fires `change`
          e.target.value = '';
        }}
      />
    </label>
  );
}
